/**
 * TenantCombobox — searchable tenant selector with server-side filtering.
 * Extracted from ProvisionVmForUserPage for reuse (e.g., SignupBonusConfigPage grant form).
 */

import { useState, useEffect } from "react";
import { ChevronsUpDown, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useTenantList } from "@/hooks/useTenantQueries";
import { cn } from "@/lib/utils";

export function TenantCombobox({
  id,
  value,
  onChange,
  disabled,
  excludeIds,
  selectedLabel,
}: {
  /** Id of the trigger, so a `<Label htmlFor>` can address the control. */
  id?: string;
  value: string;
  onChange: (id: string) => void;
  disabled?: boolean;
  /** Tenants that must not be offered — e.g. the current owner when choosing where a VM goes. */
  excludeIds?: string[];
  /**
   * Name to show for a value that is not in the loaded page of results — a tenant pre-selected from
   * the URL, or one chosen before the operator searched for something else.
   */
  selectedLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isFetching } = useTenantList({
    search: debounced || undefined,
    page: 1,
    limit: 50,
  });
  const tenants = (data?.data ?? []).filter(
    (t) => !excludeIds?.includes(t.id),
  );
  const selected = tenants.find((t) => t.id === value);
  const label = selected
    ? selected.name
    : value
      ? (selectedLabel ?? "Selected tenant")
      : "Select tenant";

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id={id}
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            // This trigger *is* the tenant field, so it follows the flat form-control contract
            // (docs/design/frontend-form-controls.md) instead of the raised outline button: same
            // height, radius, border and chrome as the SelectTrigger it sits beside. A button
            // that lifts and casts a shadow next to a flat select reads as a mistake.
            "h-9 w-full justify-between rounded-md border-input px-3 font-normal shadow-none",
            "hover:translate-y-0 hover:border-input hover:bg-transparent",
            // The empty state is a placeholder to the user even though this is a <button>.
            !selected && !value && "text-placeholder",
          )}
        >
          <span className="truncate">{label}</span>
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-(--radix-popover-trigger-width) p-0"
        align="start"
      >
        <div className="border-b p-2">
          <Input
            placeholder="Search tenants by name or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
        </div>
        <div className="max-h-64 overflow-auto p-1">
          {isFetching && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              Loading…
            </div>
          )}
          {!isFetching && tenants.length === 0 && (
            <div className="px-3 py-2 text-sm text-muted-foreground">
              No tenants found.
            </div>
          )}
          {tenants.map((t) => (
            <button
              key={t.id}
              type="button"
              className={cn(
                "flex w-full cursor-pointer items-center justify-between rounded-sm px-3 py-2 text-left text-sm hover:bg-accent",
                t.id === value && "bg-accent",
              )}
              onClick={() => {
                onChange(t.id);
                setOpen(false);
                setSearch("");
              }}
            >
              <span className="truncate">
                {t.name}
                {t.ownerEmail ? (
                  <span className="ml-2 text-muted-foreground">
                    {t.ownerEmail}
                  </span>
                ) : null}
              </span>
              {t.id === value && <Check className="h-4 w-4" />}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
